// Siparis karliligi: Etsy'nin Earnings dokumu (ledger'dan) + urun/kargo maliyeti.
// Etsy verisi siparis basina bir kez cekilip sunucuda saklanir; "yeniden cek"
// yalniz elle (iade vb. sonrasi) tetiklenir.
// Maliyetler otomatik bulunur (listing varyasyon maliyeti + katalog kargosu);
// elle degistirilen deger siparise kaydedilir, bos birakilan otomatige doner.

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TrendingUp, Loader2, Save, RotateCcw, ChevronDown, ChevronUp, Info, RefreshCw } from "lucide-react";
import { clsx } from "clsx";
import { authHeaders, getBackendUrl } from "../../lib/http";

interface Line { key: string; label: string; amount: number }
interface ItemCost {
  transactionId: number; title: string; variation: string; quantity: number;
  unitCost: number | null; unitShipping: number | null;
}
interface OrderProfit {
  currency: string;
  buyerPaid: { items: number; shipping: number; discount: number; giftWrap: number; tax: number; total: number };
  revenue: number;
  fees: Line[]; feesTotal: number;
  marketing: Line[]; marketingTotal: number;
  hidden: Line[]; hiddenTotal: number;
  etsyEarned: number;
  ledgerFound: boolean;
  costs: {
    items: ItemCost[];
    auto: { productCost: number | null; shippingCost: number | null };
    saved: { productCost: number | null; shippingCost: number | null; extraCost: number | null; note: string | null } | null;
    productCost: number | null; shippingCost: number | null; extraCost: number;
  };
  netProfit: number | null;
  margin: number | null;
  snapshotAt: string | null;
}

async function fetchProfit(receiptId: number, refresh = false): Promise<OrderProfit> {
  const res = await fetch(`${getBackendUrl()}/api/etsy/receipts/${receiptId}/profit${refresh ? "?refresh=1" : ""}`, { headers: authHeaders() });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || j.message || "Kârlılık alınamadı");
  return j.data;
}

async function saveCosts(receiptId: number, body: { productCost: number | null; shippingCost: number | null; extraCost: number | null; note: string | null }) {
  const res = await fetch(`${getBackendUrl()}/api/etsy/receipts/${receiptId}/profit`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Kaydedilemedi");
}

const fmt = (n: number, cur: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD" }).format(n);

/** "" -> null (otomatige don), aksi halde sayi */
const parse = (s: string): number | null => {
  const v = parseFloat(s.replace(",", "."));
  return s.trim() === "" || !isFinite(v) || v < 0 ? null : v;
};

function Group({ title, total, lines, cur, note }: { title: string; total: number; lines: Line[]; cur: string; note?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-3 py-2 text-sm">
        <span className="flex items-center gap-1 font-medium text-gray-800">
          {title} {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </span>
        <span className="font-semibold text-red-600">-{fmt(total, cur)}</span>
      </button>
      {open && (
        <div className="space-y-1 border-t border-gray-100 px-3 py-2 text-xs">
          {note && <p className="mb-1 text-gray-400">{note}</p>}
          {lines.map((l) => (
            <div key={l.key} className="flex justify-between text-gray-600">
              <span>{l.label}</span><span>-{fmt(l.amount, cur)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CostInput({ label, value, auto, onChange, cur }: {
  label: string; value: string; auto: number | null; onChange: (v: string) => void; cur: string;
}) {
  return (
    <label className="block">
      <span className="mb-0.5 block text-xs text-gray-500">{label}</span>
      <input
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={auto !== null ? String(auto) : "Girilmedi"}
        className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm"
      />
      <span className="mt-0.5 block text-[11px] text-gray-400">
        {auto !== null ? `Otomatik: ${fmt(auto, cur)}` : "Otomatik bulunamadı"}
      </span>
    </label>
  );
}

export default function OrderProfitSection({ receiptId }: { receiptId: number }) {
  const qc = useQueryClient();
  const { data: p, isLoading, isError, error } = useQuery({
    queryKey: ["order-profit", receiptId],
    queryFn: () => fetchProfit(receiptId),
    staleTime: 5 * 60_000,
  });
  const refetchEtsy = useMutation({
    mutationFn: () => fetchProfit(receiptId, true),
    onSuccess: (d) => {
      qc.setQueryData(["order-profit", receiptId], d);
      qc.invalidateQueries({ queryKey: ["etsy-receipts"] });
    },
  });

  // Gosterilen deger: kayitli ya da otomatik — kullanici dogrudan duzenler
  const [product, setProduct] = useState("");
  const [shipping, setShipping] = useState("");
  const [extra, setExtra] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!p) return;
    const str = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));
    setProduct(str(p.costs.productCost));
    setShipping(str(p.costs.shippingCost));
    setExtra(p.costs.extraCost ? String(p.costs.extraCost) : "");
    setNote(p.costs.saved?.note ?? "");
  }, [p]);

  const save = useMutation({
    mutationFn: (body: Parameters<typeof saveCosts>[1]) => saveCosts(receiptId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order-profit", receiptId] });
      qc.invalidateQueries({ queryKey: ["etsy-receipts"] });
    },
  });

  if (isLoading) {
    return <div className="flex items-center gap-2 py-4 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Karlılık hesaplanıyor…</div>;
  }
  if (isError || !p) return <p className="text-xs text-red-600">{(error as Error)?.message || "Karlılık alınamadı"}</p>;

  const cur = p.currency;
  const pc = parse(product), sc = parse(shipping), ec = parse(extra) ?? 0;
  const known = pc !== null && sc !== null;
  const net = Math.round((p.etsyEarned - p.hiddenTotal - (pc ?? 0) - (sc ?? 0) - ec) * 100) / 100;
  const margin = p.revenue > 0 ? (net / p.revenue) * 100 : 0;

  // Otomatikle ayni olan alan otomatige baglı kalir (null kaydedilir)
  const same = (a: number | null, b: number | null) => a !== null && b !== null && Math.abs(a - b) < 0.005;
  const body = {
    productCost: same(pc, p.costs.auto.productCost) ? null : pc,
    shippingCost: same(sc, p.costs.auto.shippingCost) ? null : sc,
    extraCost: ec || null,
    note: note.trim() || null,
  };
  const s = p.costs.saved;
  const dirty = (body.productCost ?? null) !== (s?.productCost ?? null)
    || (body.shippingCost ?? null) !== (s?.shippingCost ?? null)
    || (body.extraCost ?? null) !== (s?.extraCost ?? null)
    || (body.note ?? null) !== (s?.note ?? null);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <TrendingUp className="h-3.5 w-3.5" /> Karlılık
        </h3>
        <div className="flex items-center gap-2 text-[11px] text-gray-400">
          {p.snapshotAt && <span>Etsy verisi: {new Date(p.snapshotAt).toLocaleString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
          <button
            onClick={() => refetchEtsy.mutate()}
            disabled={refetchEtsy.isPending}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-gray-100 hover:text-gray-700"
            title="İade vb. sonradan değişiklik varsa Etsy'den yeniden çek"
          >
            <RefreshCw className={clsx("h-3 w-3", refetchEtsy.isPending && "animate-spin")} /> Etsy'den yeniden çek
          </button>
        </div>
      </div>

      <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
          <span className="font-medium text-gray-800">Alıcı ödedi {p.buyerPaid.tax > 0 && <span className="text-xs font-normal text-gray-400">(vergi hariç)</span>}</span>
          <span className="font-semibold text-gray-900">{fmt(p.revenue, cur)}</span>
        </div>
        {p.marketing.length > 0 && <Group title="Pazarlama" total={p.marketingTotal} lines={p.marketing} cur={cur} />}
        {p.fees.length > 0 && <Group title="Ücretler ve KDV" total={p.feesTotal} lines={p.fees} cur={cur} />}
        {!p.ledgerFound && (
          <p className="flex items-center gap-1 text-xs text-amber-700">
            <Info className="h-3.5 w-3.5" /> Etsy kesintileri henüz hesaba yansımamış olabilir.
          </p>
        )}
        <div className="flex justify-between px-1 text-sm">
          <span className="text-gray-600">Etsy'ye göre kazanç</span>
          <span className="font-semibold text-emerald-700">{fmt(p.etsyEarned, cur)}</span>
        </div>
        {p.hidden.length > 0 && (
          <Group title="Diğer Etsy kesintileri" total={p.hiddenTotal} lines={p.hidden} cur={cur}
            note="Etsy'nin sipariş sayfasında görünmez, faturada kesilir." />
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="grid grid-cols-3 gap-2">
            <CostInput label="Ürün maliyeti" value={product} auto={p.costs.auto.productCost} onChange={setProduct} cur={cur} />
            <CostInput label="Kargo maliyeti" value={shipping} auto={p.costs.auto.shippingCost} onChange={setShipping} cur={cur} />
            <label className="block">
              <span className="mb-0.5 block text-xs text-gray-500">Ek maliyet</span>
              <input inputMode="decimal" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="0"
                className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm" />
              <span className="mt-0.5 block text-[11px] text-gray-400">Ambalaj, hediye vb.</span>
            </label>
          </div>
          {p.costs.items.length > 0 && (
            <div className="mt-2 space-y-0.5 text-[11px] text-gray-400">
              {p.costs.items.map((i) => (
                <p key={i.transactionId} className="truncate" title={i.title}>
                  {i.quantity}× {i.variation || i.title} — ürün {i.unitCost !== null ? fmt(i.unitCost, cur) : "?"}, kargo {i.unitShipping !== null ? fmt(i.unitShipping, cur) : "?"}
                </p>
              ))}
            </div>
          )}
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (isteğe bağlı)"
            className="mt-2 w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs" />
          <div className="mt-2 flex items-center justify-end gap-2">
            {save.isError && <span className="text-xs text-red-600">{(save.error as Error).message}</span>}
            {s && (
              <button
                onClick={() => save.mutate({ productCost: null, shippingCost: null, extraCost: null, note: null })}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
              >
                <RotateCcw className="h-3 w-3" /> Otomatiğe dön
              </button>
            )}
            <button
              onClick={() => save.mutate(body)}
              disabled={!dirty || save.isPending}
              className="flex items-center gap-1 rounded-lg bg-orange-500 px-3 py-1 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-40"
            >
              {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Kaydet
            </button>
          </div>
        </div>

        <div className={clsx(
          "flex items-center justify-between rounded-lg px-3 py-2.5",
          !known ? "bg-gray-100 text-gray-500" : net >= 0 ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"
        )}>
          <span className="text-sm font-semibold">Net kâr</span>
          {known ? (
            <span className="text-lg font-bold">
              {fmt(net, cur)} <span className="text-xs font-medium">(%{margin.toFixed(1)})</span>
            </span>
          ) : (
            <span className="text-xs">Maliyetleri gir</span>
          )}
        </div>
      </div>
    </div>
  );
}
